/**
 * Shell SWR provider — Linear-style client cache for the entire Vite product.
 *
 * Port of `app/_chrome/swr-provider.tsx` (RO-10-09), generalized product-wide:
 *   1. Shared in-memory SWR map across all navigations
 *   2. localStorage persistence for bootstrap GET prefixes (every major surface)
 *   3. keepPreviousData + dedupe + focus revalidate
 *   4. MutateBridge so apiCall invalidations hit THIS cache
 *   5. Path-based enter-surface preload (not agents-only)
 *
 * Surface registry: `./product-swr-surfaces.ts`.
 */

import * as React from "react";
import { SWRConfig, useSWRConfig, type Cache, type ScopedMutator } from "swr";
import { defaultFetcher, registerBoundMutate } from "@excellent/ui/api";
import { usePathname } from "@excellent/ui/router";
import {
  PRODUCT_SWR_SURFACES,
  allPersistPrefixes,
  shouldPersistKey,
  surfaceForPath,
  type ProductSurface,
} from "./product-swr-surfaces";

const STORAGE_KEY = "excellent.swr-cache.v1";
const PERSIST_MAX_BYTES = 4_000_000;

/** @deprecated Use allPersistPrefixes() — kept for agents test compatibility. */
export const AGENTS_PERSIST_PREFIXES: readonly string[] = (() => {
  const agents = PRODUCT_SWR_SURFACES.find((s) => s.id === "agents");
  return agents ? [...agents.persistPrefixes] : [];
})();

export { PRODUCT_SWR_SURFACES, allPersistPrefixes, shouldPersistKey, surfaceForPath };
export type { ProductSurface };

function makeCacheProvider(persistPrefixes: readonly string[]): () => Cache {
  return () => {
    const map = new Map();
    if (typeof window === "undefined") return map;

    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, { data: unknown }>;
        for (const [k, v] of Object.entries(parsed)) {
          map.set(k, v);
        }
      }
    } catch {
      /* corrupt cache — start empty */
    }

    const persist = () => {
      try {
        const out: Record<string, { data: unknown }> = {};
        let bytes = 0;
        for (const [key, entry] of map.entries()) {
          if (typeof key !== "string" || !shouldPersistKey(key, persistPrefixes)) continue;
          const data = (entry as { data?: unknown })?.data;
          if (data === undefined) continue;
          const payload = JSON.stringify(data);
          bytes += payload.length + key.length;
          if (bytes > PERSIST_MAX_BYTES) break;
          out[key] = { data };
        }
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(out));
      } catch {
        /* localStorage full / disabled */
      }
    };
    window.addEventListener("beforeunload", persist);
    window.setInterval(persist, 30_000);

    return map;
  };
}

/**
 * Bind `apiCall`'s `invalidates` wiring to THIS provider's mutate. Without it,
 * mutations revalidate SWR's default cache and the custom provider never moves.
 */
function MutateBridge(): null {
  const { mutate } = useSWRConfig();
  React.useEffect(() => {
    registerBoundMutate(mutate as ScopedMutator);
    return () => registerBoundMutate(null);
  }, [mutate]);
  return null;
}

/**
 * When the pathname enters a product surface for the first time this session,
 * warm that surface's bootstrap keys into the shared SWR map.
 */
export function SurfaceBootstrapPreload(): null {
  const pathname = usePathname();
  const { mutate } = useSWRConfig();
  const warmed = React.useRef(new Set<string>());

  React.useEffect(() => {
    const surface = surfaceForPath(pathname);
    if (!surface) return;
    if (warmed.current.has(surface.id)) return;
    warmed.current.add(surface.id);
    for (const { key, load } of surface.preload) {
      void load()
        .then((data) => {
          void mutate(key, data, { revalidate: false });
        })
        .catch(() => {
          /* warm is best-effort */
        });
    }
  }, [pathname, mutate]);

  return null;
}

export function ShellSWRProvider({
  children,
  extraPersistPrefixes = [],
}: {
  children: React.ReactNode;
  /** Additional URL prefixes to persist (tests / capability overrides). */
  extraPersistPrefixes?: readonly string[];
}) {
  const prefixes = React.useMemo(
    () => [...allPersistPrefixes(), ...extraPersistPrefixes],
    [extraPersistPrefixes],
  );
  const provider = React.useMemo(() => makeCacheProvider(prefixes), [prefixes]);

  return (
    <SWRConfig
      value={{
        fetcher: defaultFetcher,
        keepPreviousData: true,
        dedupingInterval: 15_000,
        revalidateOnFocus: true,
        revalidateIfStale: true,
        refreshWhenHidden: false,
        revalidateOnMount: true,
        provider,
      }}
    >
      <MutateBridge />
      <SurfaceBootstrapPreload />
      {children}
    </SWRConfig>
  );
}

/** Prefetch a set of URLs into the SWR cache (hover / intent). */
export function prefetchUrls(urls: string[]): void {
  for (const url of urls) {
    void defaultFetcher(url).catch(() => {
      /* ignore */
    });
  }
}
